import {Box, Paper, Typography, Chip, IconButton, Pagination, Tooltip} from "@mui/material";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LinkIcon from '@mui/icons-material/Link';
import {useState, useMemo} from "react";

const CalenderListView = ({calenderData, currentDate, onFeedRedirect}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // days per page

  const colors = {
    NEWS: "#6cb1f9",
    WEEKLY_DIGEST: "#54e0e7",
    NEWS_GENERAL: "#1E90FF",
    NEWS_FINANCE: "#7dbdc9",
    SINGLE_POST: "#FFD3F4",
    ARTICLE: "#ae15fc",
    SEO: "#d48b70",
    BRANDING_BANNER: "#b8dbf0"
  };

  const FeatureType = {
    NEWS: "News",
    WEEKLY_DIGEST: "Weekly Digest",
    NEWS_GENERAL: "News General",
    NEWS_FINANCE: "News Finance",
    SINGLE_POST: "Single Post",
    ARTICLE: "Article",
    BRANDING_BANNER: "Branding Banner",
    SEO: "SEO"
  };

  const getColorByType = (type) => {
    return colors[type] ? colors[type] : "rgb(188, 195, 229)";
  };

  const getDayOfYear = (date) => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = (date - start) + ((start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000);
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  const getDateFromDayOfYear = (dayOfYear, year) => {
    const date = new Date(year, 0);
    date.setDate(dayOfYear);
    return date;
  };

  // Group calendar data by day and filter for current month
  const groupedByDay = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayOfYear = getDayOfYear(firstDayOfMonth);
    const lastDayOfYear = getDayOfYear(lastDayOfMonth);

    // Filter events for current month
    const filtered = calenderData.filter(item => {
      return item.dayOfYear >= firstDayOfYear && item.dayOfYear <= lastDayOfYear;
    });

    // Group by dayOfYear
    const grouped = {};
    filtered.forEach(item => {
      if (!grouped[item.dayOfYear]) {
        grouped[item.dayOfYear] = [];
      }
      grouped[item.dayOfYear].push(item);
    });

    // Sort events within each day by hourOfDay
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => (a.hourOfDay || 0) - (b.hourOfDay || 0));
    });

    // Convert to array and sort by day
    return Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b)
      .map(dayOfYear => ({
        dayOfYear,
        events: grouped[dayOfYear]
      }));
  }, [calenderData, currentDate]);

  // Pagination (by days)
  const totalPages = Math.ceil(groupedByDay.length / itemsPerPage);
  const paginatedDays = groupedByDay.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Reset page when month changes
  useMemo(() => {
    setPage(1);
  }, [currentDate]);

  if (groupedByDay.length === 0) {
    return (
      <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", mt: 4}}>
        <Typography sx={{color: "#70757a", fontSize: "16px"}}>
          No events scheduled for this month
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{mt: 2, px: 2}}>
      {paginatedDays.map(({dayOfYear, events}) => {
        const itemDate = getDateFromDayOfYear(dayOfYear, currentDate.getFullYear());

        return (
          <Paper
            key={dayOfYear}
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "8px",
              transition: "box-shadow 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 3
              }
            }}
          >
            <Box sx={{display: "flex", gap: 2}}>
              {/* Date Section */}
              <Box sx={{
                minWidth: "80px",
                textAlign: "center",
                borderRight: "1px solid #e0e0e0",
                pr: 2
              }}>
                <Typography sx={{fontSize: "12px", color: "#70757a", fontWeight: 500, textTransform: "uppercase"}}>
                  {itemDate.toLocaleString("en-US", {weekday: "short"})}
                </Typography>
                <Typography sx={{fontSize: "24px", fontWeight: 600, color: "rgb(60,64,67)"}}>
                  {itemDate.getDate()}
                </Typography>
                <Typography sx={{fontSize: "12px", color: "#70757a"}}>
                  {itemDate.toLocaleString("en-US", {month: "short"})}
                </Typography>
              </Box>

              {/* Events Section */}
              <Box sx={{flex: 1}}>
                {events.map((event, eventIndex) => (
                  <Box
                    key={eventIndex}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: eventIndex < events.length - 1 ? "1px solid #f0f0f0" : "none",
                      borderLeft: `3px solid ${getColorByType(event.featureType)}`,
                      pl: 1.5,
                      ml: -0.5
                    }}
                  >
                    <Box sx={{flex: 1}}>
                      <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5}}>
                        {event.hourOfDay !== undefined && (
                          <Typography sx={{fontSize: "12px", color: "#1976d2", fontWeight: 500, minWidth: "45px"}}>
                            {`${event.hourOfDay}:00`}
                          </Typography>
                        )}
                        <Chip
                          label={FeatureType[event.featureType] || event.featureType}
                          size="small"
                          sx={{
                            backgroundColor: getColorByType(event.featureType),
                            fontWeight: 500,
                            fontSize: "11px",
                            height: "22px"
                          }}
                        />
                        {event.calenderStatus === "COMPLETED" && (
                          <Tooltip title="Completed">
                            <CheckCircleRoundedIcon sx={{color: "#4caf50", fontSize: "18px"}}/>
                          </Tooltip>
                        )}
                      </Box>

                      {event.title ? (
                        <Typography sx={{fontSize: "14px", fontWeight: 500, color: "rgb(60,64,67)", ml: event.hourOfDay !== undefined ? "45px" : 0}}>
                          {event.title}
                        </Typography>
                      ) : (
                        <Typography sx={{fontSize: "14px", color: "#70757a", fontStyle: "italic", ml: event.hourOfDay !== undefined ? "45px" : 0}}>
                          No title
                        </Typography>
                      )}
                    </Box>

                    {/* Feed Link */}
                    {event.feedId && (
                      <Tooltip title="View Feed">
                        <IconButton
                          size="small"
                          onClick={() => onFeedRedirect && onFeedRedirect(event.feedId)}
                          sx={{
                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.15)"
                            }
                          }}
                        >
                          <LinkIcon sx={{fontSize: "18px", color: "#1976d2"}}/>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{display: "flex", justifyContent: "center", mt: 3, mb: 2}}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Item count */}
      <Box sx={{display: "flex", justifyContent: "center", mb: 2}}>
        <Typography sx={{fontSize: "12px", color: "#70757a"}}>
          Showing {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, groupedByDay.length)} of {groupedByDay.length} days
        </Typography>
      </Box>
    </Box>
  );
};

export default CalenderListView;
